import { StatusChipVariant } from '@/components/ui/status-chip';
import { useAxios } from '@/hooks/useAxios';
import { queryClient } from '@/queryclient';
import { useMutation, useQuery } from '@/utils/api';

// Mirrors backend/pkg/tabularium/model/engineer_vm.go. The Engineer VM is an
// ad-hoc per-(engineer, tenant) cloud workspace — a plain DynamoDB row, not a
// graph node. `status` arrives EV#-prefixed (the Status-GSI key); `phase` is a
// derived, unprefixed display token the read path augments the row with.
export interface EngineerVM {
  key: string;
  vm_id: string;
  status: string;
  phase?: string;
  tier?: string;
  instance_id?: string;
  private_ip?: string;
  launched_at?: number;
  expiry_at?: number;
  snapshot_id?: string;
  // The owning tenant (customer email, or the caller's own tenant). The list is
  // aggregated across every customer, so this is what labels each row.
  tenant?: string;
}

/**
 * Display label for a VM's owning tenant in the aggregate list. The caller's own
 * tenant is marked "(you)" so it's distinguishable from the customers.
 */
export function customerLabel(tenant: string | undefined, me: string): string {
  if (!tenant) return '—';
  return tenant === me ? `(you) ${tenant}` : tenant;
}

export const ENGINEER_VM_TIERS = ['light', 'general', 'heavy'] as const;

export type EngineerVMTier = (typeof ENGINEER_VM_TIERS)[number];

/** Derived display phases from the backend read path (contract: 8 tokens). */
export const ENGINEER_VM_PHASES = [
  'provisioning',
  'running',
  'pausing',
  'paused',
  'archiving',
  'archived',
  'reviving',
  'purged',
] as const;

export type EngineerVMPhase = (typeof ENGINEER_VM_PHASES)[number];

const TRANSITIONAL_PHASES = new Set<EngineerVMPhase>([
  'provisioning',
  'pausing',
  'archiving',
  'reviving',
]);

export function isTransitionalPhase(phase?: string): boolean {
  return !!phase && TRANSITIONAL_PHASES.has(phase as EngineerVMPhase);
}

/**
 * The backend-derived phase; anything outside the 8-token contract defaults
 * to 'provisioning' (the same default the backend read path uses).
 */
export function engineerVMPhase(
  vm: Pick<EngineerVM, 'phase'>
): EngineerVMPhase {
  return vm.phase &&
    (ENGINEER_VM_PHASES as readonly string[]).includes(vm.phase)
    ? (vm.phase as EngineerVMPhase)
    : 'provisioning';
}

const ENGINEER_VM_QUERY_KEY = ['engineer-vm'] as const;

export const engineerVMPhaseLabel: Record<EngineerVMPhase, string> = {
  provisioning: 'Provisioning',
  running: 'Running',
  pausing: 'Pausing',
  paused: 'Paused',
  archiving: 'Archiving',
  archived: 'Archived',
  reviving: 'Reviving',
  purged: 'Purged',
};

/**
 * VM-phase -> StatusChip variant. running is positive (info), the four
 * transitional phases are in-progress (high), paused/archived are inactive
 * (muted), purged is terminal (critical).
 */
export const engineerVMPhaseVariant: Record<
  EngineerVMPhase,
  StatusChipVariant
> = {
  running: 'info',
  provisioning: 'high',
  pausing: 'high',
  archiving: 'high',
  reviving: 'high',
  paused: 'muted',
  archived: 'muted',
  purged: 'critical',
};

/**
 * List the caller's Engineer VMs across every customer they can reach. It is a
 * Praetorian cross-tenant overview (`?allTenants=true`), so it does NOT
 * impersonate — the backend resolves the caller's subtenants and fans the query
 * across them. Each row carries its owning `tenant` for the Customer column.
 */
export function useEngineerVMs(enabled = true) {
  const axios = useAxios({ doNotImpersonate: true });
  return useQuery<EngineerVM[]>({
    defaultErrorMessage: 'Unable to fetch engineer VMs.',
    getErrorFromResponse: true,
    queryKey: ENGINEER_VM_QUERY_KEY,
    queryFn: async () => {
      const { data } = await axios.get('/engineer-vm?allTenants=true');
      return data?.engineer_vms ?? [];
    },
    enabled,
    staleTime: 15_000,
    // Poll while any VM is mid-transition; stop once the fleet is settled.
    refetchInterval: query => {
      const vms = query.state.data ?? [];
      return vms.some(vm => isTransitionalPhase(engineerVMPhase(vm)))
        ? 5_000
        : false;
    },
  });
}

function invalidateEngineerVMs() {
  queryClient.invalidateQueries({ queryKey: ENGINEER_VM_QUERY_KEY });
}

export interface LaunchEngineerVMInput {
  tier: EngineerVMTier;
  // Owning tenant to launch into (a customer email, or the caller's own tenant).
  // Sent as the `account` header; the backend requires admin-in-that-tenant.
  tenant: string;
}

export function useLaunchEngineerVM() {
  // Never ride the global impersonation state — the target tenant is explicit
  // per request via the account header (self-tenant is a backend no-op).
  const axios = useAxios({ doNotImpersonate: true });
  return useMutation<EngineerVM, Error, LaunchEngineerVMInput>({
    defaultErrorMessage: 'Failed to launch engineer VM.',
    getErrorFromResponse: true,
    mutationFn: async input => {
      const { data } = await axios.post(
        '/engineer-vm',
        { tier: input.tier },
        { headers: { account: input.tenant } }
      );
      return data as EngineerVM;
    },
    onSuccess: invalidateEngineerVMs,
  });
}

export type EngineerVMAction =
  | 'pause'
  | 'resume'
  | 'extend'
  | 'archive'
  | 'revive';

/** Extend sends +7d (168h), matching the backend RUNNING-stage ceiling. */
export const ENGINEER_VM_EXTEND_HOURS = 168;

export interface EngineerVMActionInput {
  vmId: string;
  action: EngineerVMAction;
  hours?: number;
  // Owning tenant of the VM — the row's `tenant`. Sent as the account header so
  // the action lands in the customer's partition where the VM actually lives.
  tenant: string;
}

export function useEngineerVMAction() {
  const axios = useAxios({ doNotImpersonate: true });
  return useMutation<EngineerVM, Error, EngineerVMActionInput>({
    defaultErrorMessage: 'Engineer VM action failed.',
    getErrorFromResponse: true,
    mutationFn: async ({ vmId, action, hours, tenant }) => {
      const config = { headers: { account: tenant } };
      if (action === 'archive') {
        const { data } = await axios.delete(`/engineer-vm/${vmId}`, config);
        return data as EngineerVM;
      }
      // revive relaunches the same row from its own snapshot.
      const route = action === 'revive' ? 'restore' : action;
      const body = action === 'extend' && hours ? { hours } : {};
      const { data } = await axios.post(
        `/engineer-vm/${vmId}/${route}`,
        body,
        config
      );
      return data as EngineerVM;
    },
    onSuccess: invalidateEngineerVMs,
  });
}

export interface CodeServerToken {
  token: string;
  vm_id: string;
  gateway_url: string;
}

/** Normalize a gateway base to https (the broker may return a bare host). */
export function buildCodeServerUrl(gateway: string, token: string): string {
  let base = (gateway || '').trim();
  if (base.startsWith('wss://'))
    base = 'https://' + base.slice('wss://'.length);
  else if (base.startsWith('ws://'))
    base = 'http://' + base.slice('ws://'.length);
  else if (!/^https?:\/\//.test(base)) base = 'https://' + base;
  return `${base.replace(/\/$/, '')}/code-server/?token=${encodeURIComponent(token)}`;
}

export interface CodeServerTokenInput {
  vmId: string;
  // Owning tenant of the VM — sent as the account header so the token is minted
  // in the customer's partition where the VM lives.
  tenant: string;
}

export function useCodeServerToken() {
  const axios = useAxios({ doNotImpersonate: true });
  return useMutation<CodeServerToken, Error, CodeServerTokenInput>({
    defaultErrorMessage: 'Failed to open code-server.',
    getErrorFromResponse: true,
    mutationFn: async ({ vmId, tenant }) => {
      const { data } = await axios.post(
        `/engineer-vm/${vmId}/code-server-token`,
        {},
        { headers: { account: tenant } }
      );
      return data as CodeServerToken;
    },
  });
}
